import React, { useState } from 'react';
import { 
  Search, Filter, Download, ArrowUpRight, ArrowDownRight, CreditCard, 
  ChevronLeft, ChevronRight, Calendar, User, ShoppingBag, Wallet,
  TrendingUp, Clock, CheckCircle, XCircle, AlertCircle, Eye,
  DollarSign, Banknote, Smartphone, Landmark
} from 'lucide-react';

const Payments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Static Mock Data
  const payments = [
    { 
      id: 'TXN-98231', orderId: '#ORD-7829', customer: 'Rahul Sharma', 
      amount: 2450, method: 'Credit Card', status: 'Completed', 
      date: 'Oct 24, 2023', time: '14:30', customerAvatar: 'R'
    },
    { 
      id: 'TXN-98232', orderId: '#ORD-7830', customer: 'Priya Patel', 
      amount: 1200, method: 'UPI', status: 'Completed', 
      date: 'Oct 25, 2023', time: '09:15', customerAvatar: 'P'
    },
    { 
      id: 'TXN-98233', orderId: '#ORD-7831', customer: 'Amit Singh', 
      amount: 4890, method: 'Net Banking', status: 'Pending', 
      date: 'Oct 25, 2023', time: '16:45', customerAvatar: 'A'
    },
    { 
      id: 'TXN-98234', orderId: '#ORD-7832', customer: 'Sneha Gupta', 
      amount: 850, method: 'Wallet', status: 'Refunded', 
      date: 'Oct 26, 2023', time: '11:20', customerAvatar: 'S'
    },
    { 
      id: 'TXN-98235', orderId: '#ORD-7833', customer: 'Vikram Verma', 
      amount: 3100, method: 'UPI', status: 'Failed', 
      date: 'Oct 26, 2023', time: '18:05', customerAvatar: 'V'
    },
    { 
      id: 'TXN-98236', orderId: '#ORD-7834', customer: 'Neha Singh', 
      amount: 5200, method: 'Credit Card', status: 'Completed', 
      date: 'Oct 27, 2023', time: '10:30', customerAvatar: 'N'
    },
  ];

  const stats = {
    totalRevenue: payments.reduce((sum, p) => sum + (p.status === 'Completed' ? p.amount : 0), 0),
    pendingAmount: payments.reduce((sum, p) => sum + (p.status === 'Pending' ? p.amount : 0), 0),
    refundAmount: payments.reduce((sum, p) => sum + (p.status === 'Refunded' ? p.amount : 0), 0),
    totalTransactions: payments.length,
    completedCount: payments.filter(p => p.status === 'Completed').length,
    pendingCount: payments.filter(p => p.status === 'Pending').length,
    refundCount: payments.filter(p => p.status === 'Refunded').length,
    failedCount: payments.filter(p => p.status === 'Failed').length
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Completed': return { label: 'Completed', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { label: 'Pending', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'Refunded': return { label: 'Refunded', icon: ArrowDownRight, color: '#ef4444', bg: '#fee2e2' };
      case 'Failed': return { label: 'Failed', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'Credit Card': return CreditCard;
      case 'Debit Card': return CreditCard;
      case 'UPI': return Smartphone;
      case 'Net Banking': return Landmark;
      case 'Wallet': return Wallet;
      default: return Banknote;
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          payment.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          payment.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    const matchesMethod = filterMethod === 'all' || payment.method === filterMethod;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const paginatedPayments = filteredPayments.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor, suffix }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? (suffix === '₹' ? `₹${value.toLocaleString()}` : value.toLocaleString()) : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  return (
    <div className="admin-payments-modern">
      {/* Header */}
      <div className="payments-header">
        <div>
          <h1 className="page-title">Payments Ledger</h1>
          <p className="page-subtitle">Track all incoming transactions from customers</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Ledger
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Revenue" value={stats.totalRevenue} icon={TrendingUp} color="#10b981" bgColor="#d1fae5" suffix="₹" />
        <StatCard title="Pending Payments" value={stats.pendingAmount} icon={Clock} color="#f59e0b" bgColor="#fed7aa" suffix="₹" />
        <StatCard title="Refunded Amount" value={stats.refundAmount} icon={ArrowDownRight} color="#ef4444" bgColor="#fee2e2" suffix="₹" />
        <StatCard title="Total Transactions" value={stats.totalTransactions} icon={CreditCard} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by Transaction ID, Order ID or Customer..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Completed">Completed</option>
            <option value="Pending">Pending</option>
            <option value="Refunded">Refunded</option>
            <option value="Failed">Failed</option>
          </select>
          <select className="filter-select" value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
            <option value="all">All Methods</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Debit Card">Debit Card</option>
            <option value="UPI">UPI</option>
            <option value="Net Banking">Net Banking</option>
            <option value="Wallet">Wallet</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredPayments.length} Transactions Found</span>
      </div>

      {/* Payments Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Date & Time</th>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayments.map((payment) => {
                const statusConfig = getStatusConfig(payment.status);
                const StatusIcon = statusConfig.icon;
                const MethodIcon = getMethodIcon(payment.method);
                
                return (
                  <tr key={payment.id}>
                    <td className="txn-id">{payment.id}</td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={12} />
                        <span>{payment.date}</span>
                        <span className="time">{payment.time}</span>
                      </div>
                    </td>
                    <td className="order-id">{payment.orderId}</td>
                    <td className="customer-cell">
                      <div className="customer-avatar">
                        <span>{payment.customerAvatar}</span>
                      </div>
                      <span>{payment.customer}</span>
                    </td>
                    <td className="method-cell">
                      <span className="method-badge">
                        <MethodIcon size={12} />
                        {payment.method}
                      </span>
                    </td>
                    <td className="amount-cell">₹{payment.amount.toLocaleString()}</td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn view" title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPayments.length)} of {filteredPayments.length} transactions
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default Payments;